import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getAllCategories, deleteCategory } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Spinner } from '../../components/ui/Spinner'
import { PageHeader } from '../../components/ui/PageHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmModal, useConfirm } from '../../components/ui/ConfirmModal'

export default function CategoryList() {
  const qc = useQueryClient()
  const { confirm, close, state: confirmState } = useConfirm()
  const { data, isLoading } = useQuery({ queryKey: ['categories'], queryFn: getAllCategories })

  const deleteMut = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => { qc.invalidateQueries(['categories']); toast.success('Category deleted') },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="Categories"
        description="Manage business categories"
        action={
          <Link to="/categories/new">
            <Button><i className="ti ti-plus" /> New Category</Button>
          </Link>
        }
      />

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.length ? (
        <EmptyState
          icon="ti-category"
          title="No categories yet"
          description="Create your first category to organize businesses"
          action={<Link to="/categories/new"><Button size="sm">Add Category</Button></Link>}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Category','Slug','Icon','Color','Order','Status',''].map(h => (
                  <th key={h} className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.map(c => (
                <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4 font-medium text-gray-900">{c.label}</td>
                  <td className="px-5 py-4">
                    <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{c.slug}</code>
                  </td>
                  <td className="px-5 py-4">
                    {c.icon && <i className={`ti ${c.icon} text-lg text-gray-600`} />}
                  </td>
                  <td className="px-5 py-4">
                    {c.color && (
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-5 h-5 rounded-md border border-gray-200 shadow-sm" style={{ background: c.color }} />
                        <span className="text-xs text-gray-400">{c.color}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-gray-500">{c.order}</td>
                  <td className="px-5 py-4">
                    <Badge color={c.isActive ? 'green' : 'gray'}>{c.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 justify-end">
                      <Link to={`/categories/${c.id}/edit`}>
                        <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600">
                          <i className="ti ti-pencil" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="xs" className="text-gray-400 hover:text-red-600"
                        onClick={() => confirm({ title: 'Delete Category', message: `Delete "${c.label}"? This action cannot be undone.`, onConfirm: () => deleteMut.mutate(c.id) })}>
                        <i className="ti ti-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
